import { useEffect, useRef } from "react";

const COLORS = [
  ["#6E9A57", "#46703E"],
  ["#8FAE5D", "#5C8A4E"],
  ["#A9CB7E", "#6E9A57"],
  ["#C8A24A", "#96762C"],
  ["#B5793B", "#85562A"],
];

/* Full-page canvas of realistically fluttering leaves.
   Scroll velocity acts as wind: faster scrolling = more leaves, stronger drift. */
export const FallingLeaves = () => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w, h, raf;

    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    let lastScroll = window.scrollY;
    let wind = 0;
    const onScroll = () => {
      const d = window.scrollY - lastScroll;
      lastScroll = window.scrollY;
      wind += d * 0.05;
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    const newLeaf = (anywhere) => {
      const [c1, c2] = COLORS[(Math.random() * COLORS.length) | 0];
      return {
        x: Math.random() * w,
        y: anywhere ? Math.random() * h : -30 - Math.random() * 60,
        size: 5 + Math.random() * 9,
        vy: 0.35 + Math.random() * 0.75,
        vx: -0.3 + Math.random() * 0.6,
        angle: Math.random() * Math.PI * 2,
        spin: (-0.5 + Math.random()) * 0.022,
        phase: Math.random() * Math.PI * 2,
        flutter: 0.025 + Math.random() * 0.03,
        swayAmp: 0.5 + Math.random() * 1.1,
        swayFreq: 0.012 + Math.random() * 0.02,
        t: Math.random() * 1000,
        c1,
        c2,
      };
    };

    const leaves = [];
    for (let i = 0; i < 14; i++) leaves.push(newLeaf(true));

    const draw = (lf) => {
      ctx.save();
      ctx.translate(lf.x, lf.y);
      ctx.rotate(lf.angle);
      const flip = Math.sin(lf.phase);
      ctx.scale(1, 0.3 + 0.7 * Math.abs(flip));
      const s = lf.size;
      const grad = ctx.createLinearGradient(0, -s, 0, s);
      grad.addColorStop(0, lf.c1);
      grad.addColorStop(1, lf.c2);
      ctx.fillStyle = grad;
      ctx.globalAlpha = 0.88;
      ctx.shadowColor = "rgba(18,42,29,0.18)";
      ctx.shadowBlur = 3;
      ctx.shadowOffsetY = 2;
      ctx.beginPath();
      ctx.moveTo(0, -s);
      ctx.bezierCurveTo(s * 0.9, -s * 0.55, s * 0.7, s * 0.55, 0, s);
      ctx.bezierCurveTo(-s * 0.7, s * 0.55, -s * 0.9, -s * 0.55, 0, -s);
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.globalAlpha = 0.5;
      ctx.strokeStyle = lf.c2;
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(0, -s * 0.95);
      ctx.lineTo(0, s * 1.3);
      ctx.stroke();
      ctx.restore();
    };

    const tick = () => {
      ctx.clearRect(0, 0, w, h);
      wind *= 0.94;
      const gust = Math.max(-7, Math.min(7, wind));
      const target = 14 + Math.min(42, Math.round(Math.abs(gust) * 8));
      while (leaves.length < target) leaves.push(newLeaf(false));

      for (let i = leaves.length - 1; i >= 0; i--) {
        const lf = leaves[i];
        lf.t++;
        lf.x += lf.vx + gust * 0.55 + Math.sin(lf.t * lf.swayFreq) * lf.swayAmp * 0.3;
        lf.y += lf.vy + Math.abs(gust) * 0.22 + Math.cos(lf.t * lf.swayFreq * 0.7) * 0.18;
        lf.angle += lf.spin + gust * 0.0035;
        lf.phase += lf.flutter + Math.abs(gust) * 0.004;

        if (lf.y > h + 50 || lf.x < -80 || lf.x > w + 80) {
          if (leaves.length > target) {
            leaves.splice(i, 1);
            continue;
          }
          leaves[i] = newLeaf(false);
          continue;
        }
        draw(lf);
      }
      raf = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      data-testid="falling-leaves-canvas"
      className="fixed inset-0 z-30 pointer-events-none"
    />
  );
};
